// =============================================================================
// canonicalTables.js — Single source of truth for the student-facing canonical
// evidence table set. Shared between @ai-insight/api (prompt filtering, post-
// processor) and @ai-insight/client (Evidence Library, legacy-remap UX).
//
// Why centralized (Phase 2 promotion):
//   Originally lived under packages/client/src/utils/canonicalTables.js. Phase 2
//   needs the api to filter the prompt by CANONICAL_TABLE_SET and remap legacy
//   IDs in the post-processor, so both sides must agree bit-for-bit. A shared
//   workspace package eliminates drift without requiring a build step (CRA's
//   webpack consumes CommonJS transparently; Node in api does native require).
//
// Shape: 20 logical report cards backed by 23 data tables.
//   - 2 global cards (Current Readiness Snapshot, Readiness History)
//   - 3 sections × 6 cards each (Qn. Selection Current, Qn. Selection History,
//     Time Management Current, Time Management History (toggle), Topic Current,
//     Topic History)
//   - Time Management History is ONE card with a Types⇄Areas toggle backed by
//     two data tables ({sec}_time_trend_type + {sec}_time_trend_area).
// =============================================================================

const SECTIONS = ['ec', 'lr', 'qadi'];
const SECTION_LABELS = { ec: 'English Comprehension', lr: 'Logical Reasoning', qadi: 'Quantitative Ability & Data Interpretation' };

// ---------------------------------------------------------------------------
// Flat canonical set — every table ID we consider part of the v2 contract.
// ---------------------------------------------------------------------------
const CANONICAL_TABLE_IDS = [
  // Global (tier-aware readiness)
  'section_breakdown',
  'cutoff_clearing_pattern',
  // Per-section × 7 data tables (6 cards; TM History is 1 card with 2 tables)
  ...SECTIONS.flatMap((sec) => [
    `${sec}_qn_selection_snapshot`,
    `${sec}_qn_selection_history`,
    `${sec}_time_current`,
    `${sec}_time_trend_type`,
    `${sec}_time_trend_area`,
    `${sec}_current_topic`,
    `${sec}_topic_history`,
  ]),
];

const CANONICAL_TABLE_SET = new Set(CANONICAL_TABLE_IDS);

// ---------------------------------------------------------------------------
// Paired "virtual" tables — used by the TM History card to drive its toggle.
// The Library emits a synthetic card ID; the modal opens with a tab control
// that lets the student switch between the two underlying data shapes.
// ---------------------------------------------------------------------------
const PAIRED_TABLES = Object.fromEntries(
  SECTIONS.map((sec) => [
    `${sec}_time_history`,
    {
      label: `${SECTION_LABELS[sec]} Time Management History`,
      tabs: [
        { key: 'byType', label: 'By Type', tableId: `${sec}_time_trend_type` },
        { key: 'byArea', label: 'By Area', tableId: `${sec}_time_trend_area` },
      ],
    },
  ])
);

// ---------------------------------------------------------------------------
// Library categories — the curated grouping rendered in Evidence Library.
// Each table entry is either:
//   - { id, label }                       → direct table, open in modal as-is
//   - { id, label, pairedKey }            → virtual paired card → toggle modal
// ---------------------------------------------------------------------------
const LIBRARY_CATEGORIES = [
  {
    name: 'Understanding Your Performance',
    tables: [
      { id: 'section_breakdown', label: 'Current Readiness Snapshot' },
      { id: 'cutoff_clearing_pattern', label: 'Readiness History' },
    ],
  },
  ...SECTIONS.map((sec) => ({
    name: `${SECTION_LABELS[sec]} Evidence`,
    tables: [
      { id: `${sec}_qn_selection_snapshot`, label: 'Current Qn. Selection' },
      { id: `${sec}_qn_selection_history`, label: 'Qn. Selection History' },
      { id: `${sec}_time_current`, label: 'Time Management Current' },
      {
        id: `${sec}_time_history`,
        label: 'Time Management History',
        pairedKey: `${sec}_time_history`,
      },
      { id: `${sec}_current_topic`, label: 'Topic Current' },
      { id: `${sec}_topic_history`, label: 'Topic History' },
    ],
  })),
];

// ---------------------------------------------------------------------------
// Legacy migration map — used by the Evidence Modal when the LLM-emitted
// report still references a deprecated table ID. During Phase 1 the backend
// still builds these tables, so the modal can either:
//   (a) transparently remap to the canonical replacement, OR
//   (b) show a "consolidated into X" notice.
// We choose (b) for honesty — silent remaps hide quality regressions.
// A null value means "no direct replacement — consult Evidence Library".
// ---------------------------------------------------------------------------
const LEGACY_TABLE_MAP = {
  // Readiness surface
  overall_test_history: 'cutoff_clearing_pattern',
  historical_varc_tests: 'cutoff_clearing_pattern',
  historical_dilr_tests: 'cutoff_clearing_pattern',
  historical_qa_tests: 'cutoff_clearing_pattern',
  historical_cutoff_pattern: 'cutoff_clearing_pattern',
  // Type-split trend tables → consolidated Qn. Selection History
  ...Object.fromEntries(
    SECTIONS.flatMap((sec) => [
      [`${sec}_type_a_trend`, `${sec}_qn_selection_history`],
      [`${sec}_type_b_trend`, `${sec}_qn_selection_history`],
      [`${sec}_type_c_trend`, `${sec}_qn_selection_history`],
      [`${sec}_type_a_history`, `${sec}_qn_selection_history`],
      [`${sec}_type_a_current`, `${sec}_qn_selection_snapshot`],
      [`${sec}_section_snapshot`, `${sec}_qn_selection_snapshot`],
      [`${sec}_area_breakdown`, `${sec}_qn_selection_snapshot`],
    ])
  ),
  // Legacy time → canonical time
  ...Object.fromEntries(
    SECTIONS.flatMap((sec) => [
      [`${sec}_time_analysis`, `${sec}_time_current`],
      [`${sec}_historical_time`, `${sec}_time_trend_type`],
      [`${sec}_time_wasters`, `${sec}_time_current`],
      [`${sec}_time_wasters_type_a`, `${sec}_time_current`],
      [`${sec}_time_wasters_type_b`, `${sec}_time_current`],
      [`${sec}_time_wasters_type_c`, `${sec}_time_current`],
    ])
  ),
  // Legacy topic → canonical topic (in case LLM still emits old stale ID)
  ...Object.fromEntries(
    SECTIONS.flatMap((sec) => [
      [`${sec}_topic_insights`, `${sec}_current_topic`],
    ])
  ),
  // Overall-level legacy with no direct replacement
  missed_type_a_questions: null,
  difficulty_split_overall: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const isCanonicalTable = (id) => CANONICAL_TABLE_SET.has(id);
const isLegacyTable = (id) => Object.prototype.hasOwnProperty.call(LEGACY_TABLE_MAP, id);
const getPairedTable = (id) => PAIRED_TABLES[id] || null;
const resolveLegacyRemap = (id) => (isLegacyTable(id) ? LEGACY_TABLE_MAP[id] : undefined);

// Friendly label lookup for use in modal copy.
const CANONICAL_LABELS = LIBRARY_CATEGORIES.flatMap((cat) => cat.tables).reduce(
  (acc, t) => ({ ...acc, [t.id]: t.label }),
  {}
);
const getCanonicalLabel = (id) => CANONICAL_LABELS[id] || id;

// Convenience: detect whether a direct tableId should be auto-upgraded to the
// paired TM History modal. The backend emits either `*_time_trend_type` or
// `*_time_trend_area` as the historical_evidence on the Time Management block;
// we want both to surface as one toggleable modal so the student sees both
// lenses without guessing which one was referenced.
function getTimeHistoryPairing(tableId) {
  if (typeof tableId !== 'string') return null;
  const m = tableId.match(/^(ec|lr|qadi)_time_trend_(type|area)$/);
  if (!m) return null;
  const [, sec, lens] = m;
  return {
    pairedKey: `${sec}_time_history`,
    activeTab: lens === 'area' ? 'byArea' : 'byType',
  };
}

module.exports = {
  CANONICAL_TABLE_IDS,
  CANONICAL_TABLE_SET,
  PAIRED_TABLES,
  LIBRARY_CATEGORIES,
  LEGACY_TABLE_MAP,
  isCanonicalTable,
  isLegacyTable,
  getPairedTable,
  resolveLegacyRemap,
  getCanonicalLabel,
  getTimeHistoryPairing,
};
